import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { competitionsService } from '../../services/competitions';
import { errorCodeOf } from '../../services/api-error';
import { cn } from '../../lib/utils';
import type { EntryTurn } from '../../types/trophy';

/** Whose turn is being chosen, as the stage page's boxes hold them. */
export interface TurnChooser {
  /** Null for the viewer themselves — the endpoint wants no `for_user_id`. */
  userId: string | null;
  fullName: string;
  /** The turn booked now, if any. */
  roundId?: string | null;
  turns: EntryTurn[];
}

interface ChooseTurnDialogProps {
  athlete: TurnChooser | null;
  competitionSlug: string;
  onCancel: () => void;
  /** Booked: the caller re-reads the entries, which moves the athlete's box. */
  onBooked: () => void;
}

/**
 * "Which turn?" — after the entry, from the stage page: an entry made before
 * the class was split into turns (or by the desk) has one to choose, and a
 * booked one can be moved while entries are open and neither turn has
 * started. The turns offered are the server's (`turns` on the entry), so this
 * holds no rule of its own; a refusal is shown under the choice, by its code.
 * A full turn is listed with its seats but cannot be picked — except the one
 * already booked, which is where the athlete is.
 */
export default function ChooseTurnDialog({ athlete, competitionSlug, onCancel, onBooked }: ChooseTurnDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [roundId, setRoundId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Opens on the turn already booked, so a change is a deliberate move. Keyed
  // on who and what is booked rather than on the object, which a caller may
  // build afresh on every render.
  const openFor = athlete ? (athlete.userId ?? 'self') : null;
  const bookedId = athlete?.roundId ?? null;
  useEffect(() => {
    setRoundId(bookedId);
    setError(null);
  }, [openFor, bookedId]);

  const turnTime = (turn: EntryTurn) => {
    if (!turn.start_time) return t('stageEntry.dialog.timeTba');
    return new Date(turn.start_time).toLocaleString(i18n.language, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const save = async () => {
    if (!athlete || !roundId || saving) return;
    setSaving(true);
    setError(null);
    try {
      await competitionsService.bookTurn(competitionSlug, roundId, athlete.userId);
      const turn = athlete.turns.find((candidate) => candidate.id === roundId);
      toast({ title: t('stageEntry.turnChoice.saved', { name: athlete.fullName, turn: turn?.name ?? '' }) });
      onBooked();
    } catch (caught) {
      const code = errorCodeOf(caught);
      setError(
        code
          ? t(`stageEntry.dialog.errors.${code}`, { defaultValue: caught instanceof Error ? caught.message : '' })
          : caught instanceof Error
            ? caught.message
            : t('stageEntry.turnChoice.error')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!athlete} onOpenChange={(open) => !open && !saving && onCancel()}>
      <DialogContent className="w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] p-4 sm:max-w-md sm:p-6">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="text-base sm:text-lg">
            {athlete?.userId
              ? t('stageEntry.turnChoice.titleFor', { name: athlete.fullName })
              : t('stageEntry.turnChoice.title')}
          </DialogTitle>
          <DialogDescription>{t('stageEntry.turnChoice.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5" role="radiogroup" aria-label={t('stageEntry.dialog.turn')}>
          {(athlete?.turns ?? []).map((turn) => {
            const closed = !!turn.full && turn.id !== athlete?.roundId;
            const seats = turn.full
              ? t('stageEntry.dialog.full')
              : turn.seats_left != null
                ? t('stageEntry.dialog.seatsLeft', { count: turn.seats_left })
                : null;
            return (
              <label
                key={turn.id}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
                  closed ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                  roundId === turn.id && 'border-primary ring-1 ring-primary'
                )}
                data-testid="turn-choice-option"
              >
                <input
                  type="radio"
                  name="turn-choice"
                  value={turn.id}
                  checked={roundId === turn.id}
                  disabled={closed}
                  onChange={() => {
                    setRoundId(turn.id);
                    setError(null);
                  }}
                  className="accent-primary"
                />
                <span className="min-w-0 flex-1 break-words">
                  {t('stageEntry.dialog.roundLine', { name: turn.name, time: turnTime(turn) })}
                </span>
                {seats && (
                  <span
                    className={cn('shrink-0 text-xs', turn.full ? 'text-destructive' : 'text-muted-foreground')}
                    data-testid="turn-choice-seats"
                  >
                    {seats}
                  </span>
                )}
              </label>
            );
          })}
          {error && (
            <p className="text-xs text-destructive" data-testid="turn-choice-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving} className="w-full sm:w-auto">
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={!roundId || roundId === athlete?.roundId || saving}
            data-testid="save-turn-choice"
            className="w-full sm:w-auto"
          >
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('stageEntry.turnChoice.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
